import { useMemo } from 'react';

import type { PaymentMethodCode } from '@src/domains/payments';
import type { LoginMode } from '@src/features/customers';

import {
  trackAccountCreated,
  trackAccountFound,
  trackAccountNotFound,
  trackCheckoutCompleted,
  trackCheckoutStarted,
  trackCodeSent,
  trackCodeValidated,
  trackEmailSubmitted,
  trackLoginCodeSelected,
  trackLoginCompleted,
  trackLoginPasswordSelected,
  trackPaymentFailed,
  trackPaymentMethodSelected,
  trackPixQrShown,
  trackThreeDsShown,
} from '../checkoutTracking';
import { useCheckoutEventProps } from './useCheckoutEventProps';

export type CheckoutTracking = {
  checkoutStarted: (isLoggedIn: boolean) => void;
  emailSubmitted: () => void;
  accountFound: () => void;
  accountNotFound: () => void;
  loginPasswordSelected: () => void;
  loginCodeSelected: () => void;
  codeSent: () => void;
  codeValidated: () => void;
  loginCompleted: (method: LoginMode) => void;
  accountCreated: () => void;
  paymentMethodSelected: (method: PaymentMethodCode) => void;
  pixQrShown: () => void;
  threeDsShown: (attemptNumber: number) => void;
  paymentFailed: (params: { reason: string; attemptNumber: number; method?: PaymentMethodCode }) => void;
  checkoutCompleted: (params: { method?: string; totalPriceCents: number }) => void;
};

export const useCheckoutTracking = (): CheckoutTracking => {
  const props = useCheckoutEventProps();

  return useMemo(
    () => ({
      checkoutStarted: (isLoggedIn: boolean) => trackCheckoutStarted(props, { isLoggedIn }),
      emailSubmitted: () => trackEmailSubmitted(props),
      accountFound: () => trackAccountFound(props),
      accountNotFound: () => trackAccountNotFound(props),
      loginPasswordSelected: () => trackLoginPasswordSelected(props),
      loginCodeSelected: () => trackLoginCodeSelected(props),
      codeSent: () => trackCodeSent(props),
      codeValidated: () => trackCodeValidated(props),
      loginCompleted: (method: LoginMode) => trackLoginCompleted(props, method),
      accountCreated: () => trackAccountCreated(props),
      paymentMethodSelected: (method: PaymentMethodCode) => trackPaymentMethodSelected(props, method),
      pixQrShown: () => trackPixQrShown(props),
      threeDsShown: (attemptNumber: number) => trackThreeDsShown(props, attemptNumber),
      paymentFailed: (params: { reason: string; attemptNumber: number; method?: PaymentMethodCode }) =>
        trackPaymentFailed(props, params),
      checkoutCompleted: (params: { method?: string; totalPriceCents: number }) =>
        trackCheckoutCompleted(props, params),
    }),
    [props],
  );
};

export default useCheckoutTracking;
